/**
 * EN/FA copy, lifted from the Claude Design so the implementation and the design share one
 * string table. Language follows the user's saved setting, falling back to what Telegram
 * reports; `dir` is derived from it and set on <html> so every logical property mirrors.
 */

import { createContext, useContext } from 'react';

export type Lang = 'en' | 'fa';

export interface Strings {
  appName: string;
  miniApp: string;

  // navigation
  home: string;
  search: string;
  favorites: string;
  profile: string;

  // home
  searchPh: string;
  potd: string;
  trending: string;
  newest: string;
  topRated: string;
  seeAll: string;
  leaderboard: string;
  leaderboardSub: string;
  /** What the creator score means, so a bare number is not presented as a mystery. */
  leaderboardCreatorScore: string;
  submitPrompt: string;
  submitSub: string;

  // search
  filters: string;
  reset: string;
  type: string;
  model: string;
  category: string;
  sort: string;
  all: string;
  trendingSort: string;
  newestSort: string;
  topSort: string;
  dlSort: string;
  results: (n: string) => string;
  /**
   * The two halves of a reported spelling correction. Split rather than interpolated so the
   * corrected term can sit in its own `<bdi>` — a Latin word inside a Persian sentence needs an
   * isolate, or the neutral characters around it reorder.
   */
  showingResultsFor: string;
  searchInsteadPrefix: string;
  noResults: string;
  noResultsSub: string;
  clearFilters: string;
  showResults: (n: string) => string;
  done: string;

  // detail
  close: string;
  playInTelegram: string;
  /** Shown under a fullscreen image until the user zooms, then it has served its purpose. */
  lightboxHint: string;
  viewFullscreen: string;
  prompt: string;
  negPrompt: string;
  howToUse: string;
  rateThis: string;
  related: string;
  tapToRate: string;
  yourRating: string;
  ratingSaved: string;
  comments: string;
  writeComment: string;
  reply: string;
  noComments: string;
  copyPrompt: string;
  copiedBtn: string;
  copied: string;
  share: string;
  save: string;
  saved: string;
  sourceMeta: string;
  /**
   * Headings that name whose numbers a reader is looking at. Parameterised on the platform so a
   * source metric can never be rendered without its attribution.
   */
  onSource: (platform: string) => string;
  viewOnSource: (platform: string) => string;
  onPromptVault: string;
  rating: string;
  saves: string;
  views: string;
  /**
   * The stat-row spelling of "downloads", lowercase.
   *
   * Separate from `downloads`, which is a Title-case label on the profile's stat tiles. The stat
   * row borrowed that one and read "8.4k views · 1.2k Downloads · 4.8 rating · 64 saves" — three
   * lowercase words and one capitalised, on one line, in one sentence-shaped row.
   */
  downloadsStat: string;
  sortSourcePopular: string;
  allSources: string;
  memberMeta: string;
  showMore: string;
  showLess: string;

  // favorites
  savedTab: string;
  collectionsTab: string;
  favEmptyTitle: string;
  favEmptySub: string;
  browsePrompts: string;
  newCollection: string;
  addToCollection: string;
  addedToCollection: string;
  collectionsEmptyTitle: string;
  collectionsEmptySub: string;
  promptsCount: (n: string) => string;
  namePrompt: string;

  // profile
  settings: string;
  language: string;
  nsfwLabel: string;
  nsfwSub: string;
  notifLabel: string;
  notifSub: string;
  supportChat: string;
  openBot: string;
  showIntro: string;
  downloads: string;
  ratingsGiven: string;
  submissions: string;
  version: string;
  goPro: string;
  proActive: string;

  // submit
  details: string;
  titleLabel: string;
  titlePh: string;
  promptLabel: string;
  promptPh: string;
  classify: string;
  tagsLabel: string;
  addTag: string;
  examples: string;
  add: string;
  submitHint: string;
  submitCta: string;
  submitDone: string;
  submitDoneSub: string;
  submitDuplicate: string;
  negPromptPh: string;
  instructionsPh: string;
  sourceUrlLabel: string;
  sourceUrlPh: string;

  // leaderboard
  lbPrompts: string;
  lbContributors: string;
  lbNoteWeek: (n: string) => string;
  lbNoteAll: (n: string) => string;
  /** The period switch. The API has always computed both boards; only one was reachable. */
  lbPeriodWeek: string;
  lbPeriodAll: string;
  lbEmpty: string;

  // author
  authorPrompts: string;
  authorDownloads: string;

  // support
  supportTitle: string;
  supportToday: string;
  supportPh: string;
  supportEmpty: string;
  supportAgent: string;

  // premium
  premium: string;
  premiumTitle: string;
  premiumSub: string;
  unlockCta: string;
  premiumFine: string;

  // states
  /**
   * Accessibility strings. These were English literals in a bilingual app: a Farsi screen reader
   * announced "Close", "Clear", "Rating" and "Remove example 2", and the numeric ones were ASCII
   * where every visible numeral on the same screen is Persian.
   */
  clear: string;
  starsOf: (n: string) => string;
  removeExample: (n: string) => string;

  retry: string;
  errorTitle: string;
  errorSub: string;
  offline: string;
  loading: string;
  /** The rotation, made touchable: a button that visibly deals a new hand of prompts. */
  showMeMore: string;
  skip: string;
  next: string;
  startBrowsing: string;
  /** Only where a reveal is actually wired — a grid card gets `nsfwBadge` instead. */
  revealNsfw: string;
  /** The label on a blurred tile that has no reveal: it states what it is, and promises nothing. */
  nsfwBadge: string;
  playVideo: string;
}

const en: Strings = {
  appName: 'PromptVault',
  miniApp: 'mini app',
  home: 'Home',
  search: 'Search',
  favorites: 'Saved',
  profile: 'Profile',
  searchPh: 'Search prompts',
  potd: 'Prompt of the day',
  trending: 'Trending',
  newest: 'New',
  topRated: 'Top rated',
  seeAll: 'See all →',
  leaderboard: 'Leaderboard',
  leaderboardCreatorScore: 'avg. rank at source',
  leaderboardSub: 'Top prompts and contributors',
  submitPrompt: 'Submit a prompt',
  submitSub: 'Share yours with the community',
  filters: 'Filters',
  reset: 'Reset',
  type: 'Type',
  model: 'Model',
  category: 'Category',
  sort: 'Sort',
  all: 'All',
  trendingSort: 'Trending',
  newestSort: 'Newest',
  topSort: 'Top rated',
  dlSort: 'Most downloaded',
  results: (n) => `${n} results`,
  showingResultsFor: 'Showing results for',
  searchInsteadPrefix: 'Search instead for',
  noResults: 'Nothing matches that',
  noResultsSub: 'Try a different model or clear your filters.',
  clearFilters: 'Clear filters',
  showResults: (n) => `Show ${n} results`,
  done: 'Done',
  close: 'Close',
  playInTelegram: '▶ Play in Telegram',
  lightboxHint: 'Pinch or double-tap to zoom · swipe down to close',
  viewFullscreen: 'View fullscreen',
  prompt: 'Prompt',
  negPrompt: 'Negative prompt',
  howToUse: 'How to use',
  rateThis: 'Rate this prompt',
  related: 'Related prompts',
  tapToRate: 'Tap a star to rate',
  yourRating: 'Your rating',
  ratingSaved: 'Rating saved',
  comments: 'Comments',
  writeComment: 'Write a comment…',
  reply: 'Reply',
  noComments: 'No comments yet. Be the first.',
  copyPrompt: 'Copy prompt',
  copiedBtn: 'Copied',
  copied: 'Copied to clipboard',
  share: 'Share',
  save: 'Save',
  saved: 'Saved',
  sourceMeta: 'Source',
  onSource: (platform) => `On ${platform}`,
  viewOnSource: (platform) => `View on ${platform} →`,
  onPromptVault: 'On PromptVault',
  rating: 'rating',
  saves: 'saves',
  views: 'views',
  downloadsStat: 'downloads',
  sortSourcePopular: 'Popular at source',
  allSources: 'All sources',
  memberMeta: 'Member profile',
  showMore: 'Show more',
  showLess: 'Show less',
  savedTab: 'Saved',
  collectionsTab: 'Collections',
  favEmptyTitle: 'Nothing saved yet',
  favEmptySub: 'Tap ♥ on any prompt and it lands here.',
  browsePrompts: 'Browse prompts',
  newCollection: 'New collection',
  addToCollection: 'Add to collection',
  addedToCollection: 'Added to collection',
  collectionsEmptyTitle: 'No collections yet',
  collectionsEmptySub: 'Group saved prompts into collections to find them faster.',
  promptsCount: (n) => `${n} prompts`,
  namePrompt: 'Name your collection',
  settings: 'Settings',
  language: 'Language',
  nsfwLabel: 'Show NSFW content',
  nsfwSub: 'Hidden by default',
  notifLabel: 'Notifications',
  notifSub: 'New prompts and updates via the bot',
  supportChat: 'Support chat',
  openBot: 'Open bot in Telegram',
  showIntro: 'Show intro again',
  downloads: 'Downloads',
  ratingsGiven: 'Ratings given',
  submissions: 'Submissions',
  version: 'PromptVault · Telegram Mini App',
  goPro: 'Get Pro',
  proActive: 'Pro active',
  details: 'Details',
  titleLabel: 'Title',
  titlePh: 'e.g. Cinematic portrait, golden-hour rim light',
  promptLabel: 'Prompt text',
  promptPh: 'Paste the exact prompt you ran…',
  classify: 'Classification',
  tagsLabel: 'Tags',
  addTag: 'Add',
  examples: 'Example outputs',
  add: 'Add',
  submitHint: 'Link up to 3 example images. Submissions are reviewed before going live.',
  submitCta: 'Submit for review',
  submitDone: 'Sent for review',
  submitDoneSub: "Your prompt is in the queue. It appears in the library once it's approved.",
  submitDuplicate: 'We already have this one — thanks anyway.',
  negPromptPh: 'What the model should avoid (optional)',
  instructionsPh: 'How to use this prompt (optional)',
  sourceUrlLabel: 'Source link',
  sourceUrlPh: 'https://… (optional)',
  lbPrompts: 'Prompts',
  lbContributors: 'Contributors',
  lbNoteWeek: (n) => `Resets every Monday · ${n} members`,
  lbNoteAll: (n) => `All time · ${n} members`,
  lbPeriodWeek: 'This week',
  lbPeriodAll: 'All time',
  lbEmpty: 'Nothing ranked yet',
  authorPrompts: 'prompts',
  authorDownloads: 'downloads',
  supportTitle: 'Support',
  supportToday: 'Today',
  supportPh: 'Message',
  supportEmpty: 'Ask us anything — we usually reply within a day.',
  supportAgent: 'PromptVault Support',
  premium: 'Pro',
  premiumTitle: 'PromptVault Pro',
  premiumSub: 'Unlimited copies, early access to new drops, and no daily cap.',
  unlockCta: 'Unlock Pro',
  premiumFine: 'Cancel anytime · Billed through Telegram Stars',
  clear: 'Clear',
  starsOf: (n) => `${n} of 5 stars`,
  removeExample: (n) => `Remove example ${n}`,
  retry: 'Try again',
  errorTitle: "That didn't load",
  errorSub: 'Check your connection and try again.',
  offline: "You're offline — showing saved content",
  loading: 'Loading',
  showMeMore: 'Show me different prompts',
  skip: 'Skip',
  next: 'Next',
  startBrowsing: 'Start browsing',
  revealNsfw: 'Tap to reveal',
  nsfwBadge: 'NSFW',
  playVideo: 'Play video',
};

const fa: Strings = {
  appName: 'پرامپت‌والت',
  miniApp: 'مینی‌اپ',
  home: 'خانه',
  search: 'جستجو',
  favorites: 'ذخیره‌ها',
  profile: 'پروفایل',
  searchPh: 'جستجوی پرامپت',
  potd: 'پرامپت روز',
  trending: 'پرطرفدار',
  newest: 'جدید',
  topRated: 'بهترین امتیاز',
  seeAll: 'همه ←',
  leaderboard: 'جدول برترین‌ها',
  leaderboardCreatorScore: 'میانگین رتبه در منبع',
  leaderboardSub: 'برترین پرامپت‌ها و سازندگان',
  submitPrompt: 'ارسال پرامپت',
  submitSub: 'پرامپت خود را با بقیه به اشتراک بگذارید',
  filters: 'فیلترها',
  reset: 'بازنشانی',
  type: 'نوع',
  model: 'مدل',
  category: 'دسته‌بندی',
  sort: 'ترتیب',
  all: 'همه',
  trendingSort: 'پرطرفدار',
  newestSort: 'جدیدترین',
  topSort: 'بهترین امتیاز',
  dlSort: 'بیشترین دانلود',
  results: (n) => `${n} نتیجه`,
  showingResultsFor: 'نمایش نتایج برای',
  searchInsteadPrefix: 'در عوض این را جستجو کنید:',
  noResults: 'چیزی پیدا نشد',
  noResultsSub: 'مدل دیگری را امتحان کنید یا فیلترها را پاک کنید.',
  clearFilters: 'پاک کردن فیلترها',
  showResults: (n) => `نمایش ${n} نتیجه`,
  done: 'تأیید',
  close: 'بستن',
  playInTelegram: '▶ پخش در تلگرام',
  lightboxHint: 'با دو انگشت یا دو ضربه بزرگ‌نمایی کنید · برای بستن به پایین بکشید',
  viewFullscreen: 'نمایش تمام‌صفحه',
  prompt: 'پرامپت',
  negPrompt: 'پرامپت منفی',
  howToUse: 'نحوه استفاده',
  rateThis: 'به این پرامپت امتیاز بدهید',
  related: 'پرامپت‌های مشابه',
  tapToRate: 'برای امتیاز روی ستاره بزنید',
  yourRating: 'امتیاز شما',
  ratingSaved: 'امتیاز ثبت شد',
  comments: 'نظرات',
  writeComment: 'نظر خود را بنویسید…',
  reply: 'پاسخ',
  noComments: 'هنوز نظری نیست. اولین نفر باشید.',
  copyPrompt: 'کپی پرامپت',
  copiedBtn: 'کپی شد',
  copied: 'در حافظه کپی شد',
  share: 'اشتراک',
  save: 'ذخیره',
  saved: 'ذخیره شد',
  sourceMeta: 'منبع',
  onSource: (platform) => `در ${platform}`,
  viewOnSource: (platform) => `مشاهده در ${platform} ←`,
  onPromptVault: 'در پرامپت‌والت',
  rating: 'امتیاز',
  saves: 'ذخیره',
  views: 'بازدید',
  downloadsStat: 'دانلود',
  sortSourcePopular: 'محبوب در منبع',
  allSources: 'همهٔ منابع',
  memberMeta: 'پروفایل کاربر',
  showMore: 'نمایش بیشتر',
  showLess: 'نمایش کمتر',
  savedTab: 'ذخیره‌ها',
  collectionsTab: 'مجموعه‌ها',
  favEmptyTitle: 'هنوز چیزی ذخیره نکرده‌اید',
  favEmptySub: 'روی ♥ هر پرامپت بزنید تا همین‌جا ذخیره شود.',
  browsePrompts: 'مرور پرامپت‌ها',
  newCollection: 'مجموعه جدید',
  addToCollection: 'افزودن به مجموعه',
  addedToCollection: 'به مجموعه افزوده شد',
  collectionsEmptyTitle: 'هنوز مجموعه‌ای ندارید',
  collectionsEmptySub: 'پرامپت‌های ذخیره‌شده را دسته‌بندی کنید تا سریع‌تر پیدایشان کنید.',
  promptsCount: (n) => `${n} پرامپت`,
  namePrompt: 'نام مجموعه',
  settings: 'تنظیمات',
  language: 'زبان',
  nsfwLabel: 'نمایش محتوای بزرگسال',
  nsfwSub: 'به‌صورت پیش‌فرض پنهان است',
  notifLabel: 'اعلان‌ها',
  notifSub: 'پرامپت‌های تازه و به‌روزرسانی‌ها از طریق ربات',
  supportChat: 'گفتگو با پشتیبانی',
  openBot: 'باز کردن ربات در تلگرام',
  showIntro: 'نمایش دوباره‌ی معرفی',
  downloads: 'دانلود',
  ratingsGiven: 'امتیاز داده',
  submissions: 'ارسال‌شده',
  version: 'پرامپت‌والت · مینی‌اپ تلگرام',
  goPro: 'تهیه نسخه پرو',
  proActive: 'پرو فعال است',
  details: 'جزئیات',
  titleLabel: 'عنوان',
  titlePh: 'مثلاً پرتره سینمایی، نور طلایی',
  promptLabel: 'متن پرامپت',
  promptPh: 'پرامپتی که اجرا کردید را دقیقاً بچسبانید…',
  classify: 'دسته‌بندی',
  tagsLabel: 'برچسب‌ها',
  addTag: 'افزودن',
  examples: 'نمونه خروجی',
  add: 'افزودن',
  submitHint: 'تا ۳ لینک تصویر نمونه اضافه کنید. ارسال‌ها را پیش از انتشار بررسی می‌کنیم.',
  submitCta: 'ارسال برای بررسی',
  submitDone: 'برای بررسی ارسال شد',
  submitDoneSub: 'پرامپت شما در صف بررسی است و پس از تأیید منتشر می‌شود.',
  submitDuplicate: 'این پرامپت را از قبل داریم — با این حال ممنون.',
  negPromptPh: 'آنچه مدل باید از آن پرهیز کند (اختیاری)',
  instructionsPh: 'نحوه استفاده از این پرامپت (اختیاری)',
  sourceUrlLabel: 'لینک منبع',
  sourceUrlPh: 'https://… (اختیاری)',
  lbPrompts: 'پرامپت‌ها',
  lbContributors: 'مشارکت‌کنندگان',
  lbNoteWeek: (n) => `هر دوشنبه بازنشانی می‌شود · ${n} عضو`,
  lbNoteAll: (n) => `از ابتدا · ${n} عضو`,
  lbPeriodWeek: 'این هفته',
  lbPeriodAll: 'از ابتدا',
  lbEmpty: 'هنوز رتبه‌بندی‌ای نیست',
  authorPrompts: 'پرامپت',
  authorDownloads: 'دانلود',
  supportTitle: 'پشتیبانی',
  supportToday: 'امروز',
  supportPh: 'پیام',
  supportEmpty: 'هر سؤالی دارید بپرسید — معمولاً ظرف یک روز پاسخ می‌دهیم.',
  supportAgent: 'پشتیبانی پرامپت‌والت',
  premium: 'پرو',
  premiumTitle: 'پرامپت‌والت پرو',
  premiumSub: 'کپی نامحدود، دسترسی زودهنگام به پرامپت‌های تازه و بدون سقف روزانه.',
  unlockCta: 'فعال‌سازی پرو',
  premiumFine: 'لغو در هر زمان · پرداخت با استارز تلگرام',
  clear: 'پاک کردن',
  starsOf: (n) => `${n} از ۵ ستاره`,
  removeExample: (n) => `حذف نمونه ${n}`,
  retry: 'تلاش دوباره',
  errorTitle: 'بارگذاری نشد',
  errorSub: 'اتصال خود را بررسی کنید و دوباره تلاش کنید.',
  offline: 'آفلاین هستید — محتوای ذخیره‌شده را نشان می‌دهیم',
  loading: 'در حال بارگذاری',
  showMeMore: 'پرامپت‌های دیگری نشانم بده',
  skip: 'رد کردن',
  next: 'بعدی',
  startBrowsing: 'شروع مرور',
  revealNsfw: 'برای نمایش بزنید',
  nsfwBadge: 'محتوای بزرگسال',
  playVideo: 'پخش ویدیو',
};

export const STRINGS: Record<Lang, Strings> = { en, fa };

/** Localized labels for PROMPT_TYPES. Kept beside the string tables rather than in Strings
 *  so adding a prompt type is a one-line change in each language. */
const TYPE_LABELS: Record<Lang, Record<string, string>> = {
  en: {
    image: 'Image',
    video: 'Video',
    text: 'Text',
    code: 'Code',
    audio: 'Audio',
    music: 'Music',
    '3d': '3D',
    design: 'Design',
    agent: 'Agent',
    other: 'Other',
  },
  fa: {
    image: 'تصویر',
    video: 'ویدیو',
    text: 'متن',
    code: 'کد',
    audio: 'صدا',
    music: 'موسیقی',
    '3d': 'سه‌بعدی',
    design: 'طراحی',
    agent: 'عامل',
    other: 'سایر',
  },
};

export function promptTypeLabel(type: string, lang: Lang): string {
  return TYPE_LABELS[lang][type] ?? type;
}

export const RTL_LANGS = new Set(['fa', 'ar', 'he', 'ur', 'ps']);

export function isRtl(lang: string): boolean {
  return RTL_LANGS.has(lang);
}

export function normalizeLang(raw: string | null | undefined): Lang {
  return raw === 'fa' ? 'fa' : 'en';
}

export interface I18n {
  lang: Lang;
  rtl: boolean;
  t: Strings;
  setLang: (lang: Lang) => void;
}

export const I18nContext = createContext<I18n>({
  lang: 'en',
  rtl: false,
  t: en,
  setLang: () => {},
});

export function useI18n(): I18n {
  return useContext(I18nContext);
}
